import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- Auth ----
export const setAuthToken = (token) => {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
};

export const authRegister = async (email, password, name) => {
  const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
  return data;
};

export const authLogin = async (email, password) => {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  return data;
};

export const authMe = async () => {
  const { data } = await axios.get(`${API}/auth/me`);
  return data.user;
};

export const adminListUsers = async () => {
  const { data } = await axios.get(`${API}/admin/users`);
  return data;
};

export const adminUpdateUser = async (id, patch) => {
  const { data } = await axios.patch(`${API}/admin/users/${id}`, patch);
  return data;
};

export const adminDeleteUser = async (id) => {
  const { data } = await axios.delete(`${API}/admin/users/${id}`);
  return data;
};

export const translateBatch = async (texts, target_language, target_code) => {
  const { data } = await axios.post(`${API}/translate`, { texts, target_language, target_code });
  return data.translations;
};

export const synthesizeSpeech = async (text, language, speed = 1.0) => {
  const { data } = await axios.post(`${API}/tts`, { text, language, speed });
  return data.audio_base64;
};

export const askCinemaAI = async (message, session_id, context, reply_language = "English") => {
  const { data } = await axios.post(`${API}/ai/chat`, { message, session_id, context, reply_language });
  return data;
};

export const getAiFaq = async (tag, limit = 8) => {
  const { data } = await axios.get(`${API}/ai/faq`, { params: { tag, limit } });
  return data;
};

export const recommendMovies = async (payload) => {
  const { data } = await axios.post(`${API}/recommend`, payload);
  return data;
};

export const createSession = async (email, name) => {
  const { data } = await axios.post(`${API}/user/session`, { email, name });
  return data;
};

export const fetchMyCinema = async (userId) => {
  const { data } = await axios.get(`${API}/mycinema/${userId}`);
  return data;
};

export const toggleList = async (userId, list_name, item_id) => {
  const { data } = await axios.post(`${API}/mycinema/${userId}/toggle`, { list_name, item_id });
  return data;
};

export const rateMovie = async (userId, movie_id, score) => {
  const { data } = await axios.post(`${API}/mycinema/${userId}/rate`, { movie_id, score });
  return data;
};

// ---- CMS: multilingual content ----
export const createContent = async (payload) => {
  const { data } = await axios.post(`${API}/content`, payload);
  return data;
};

export const listContent = async (status, type) => {
  const params = {};
  if (status) params.status = status;
  if (type) params.type = type;
  const { data } = await axios.get(`${API}/content`, { params });
  return data;
};

export const getContent = async (id) => {
  const { data } = await axios.get(`${API}/content/${id}`);
  return data;
};

export const updateContent = async (id, payload) => {
  const { data } = await axios.put(`${API}/content/${id}`, payload);
  return data;
};

export const moderateContent = async (id, action) => {
  const { data } = await axios.post(`${API}/content/${id}/moderate`, { action });
  return data;
};

export const deleteContent = async (id) => {
  const { data } = await axios.delete(`${API}/content/${id}`);
  return data;
};

export const translateFields = async (fields, target_language, target_code) => {
  const { data } = await axios.post(`${API}/content/translate-fields`, { fields, target_language, target_code });
  return data.fields;
};

export const getIndustry = async (lang) => {
  const { data } = await axios.get(`${API}/industry/${lang}`);
  return data;
};

export const listEditorials = async () => {
  const { data } = await axios.get(`${API}/editorial`);
  return data;
};

export const getEditorial = async (slug) => {
  const { data } = await axios.get(`${API}/editorial/${slug}`);
  return data;
};

export const listPublished = async () => {
  const { data } = await axios.get(`${API}/content`, { params: { status: "approved" } });
  return data.items || [];
};

// ---- Discussions (comments + reactions + photos) ----
export const listDiscussions = async (entityType, entityId) => {
  const { data } = await axios.get(`${API}/discussions`, {
    params: { entity_type: entityType, entity_id: entityId },
  });
  return data;
};

export const postComment = async (payload) => {
  const { data } = await axios.post(`${API}/discussions`, payload);
  return data;
};

export const editComment = async (id, body) => {
  const { data } = await axios.patch(`${API}/discussions/${id}`, { body });
  return data;
};

export const deleteComment = async (id) => {
  const { data } = await axios.delete(`${API}/discussions/${id}`);
  return data;
};

export const reactComment = async (id, type) => {
  const { data } = await axios.post(`${API}/discussions/${id}/react`, { type });
  return data;
};

export const reportComment = async (id, reason = "") => {
  const { data } = await axios.post(`${API}/discussions/${id}/report`, { reason });
  return data;
};

export const uploadDiscussionPhoto = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/discussions/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const adminListDiscussions = async (params = {}) => {
  const { data } = await axios.get(`${API}/admin/discussions`, { params });
  return data;
};

// Resolve a photo URL returned by the API (starts with /api/...) to an absolute URL.
export const fileUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

// ---- MalayalamCinema CMS ----
export const mlAdminList = async (section) =>
  (await axios.get(`${API}/ml/admin/content`, { params: section ? { section } : {} })).data;
export const mlAdminGet = async (id) => (await axios.get(`${API}/ml/admin/content/${id}`)).data;
export const mlCreate = async (payload) => (await axios.post(`${API}/ml/content`, payload)).data;
export const mlUpdate = async (id, payload) => (await axios.put(`${API}/ml/content/${id}`, payload)).data;
export const mlDelete = async (id) => (await axios.delete(`${API}/ml/content/${id}`)).data;
export const mlPublicList = async (section, limit = 100) =>
  (await axios.get(`${API}/ml/content`, { params: { section, limit } })).data;
export const mlBySlug = async (section, slug) =>
  (await axios.get(`${API}/ml/content/by-slug/${section}/${slug}`)).data;
export const mlUpload = async (file) => {
  const f = new FormData();
  f.append("file", file);
  return (await axios.post(`${API}/ml/upload`, f, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const mlMediaList = async (kind, q) =>
  (await axios.get(`${API}/ml/media`, { params: { kind, q } })).data;
export const mlMediaCreate = async (payload) => (await axios.post(`${API}/ml/media`, payload)).data;
export const mlMediaUpdate = async (id, payload) => (await axios.put(`${API}/ml/media/${id}`, payload)).data;
export const mlMediaDelete = async (id) => (await axios.delete(`${API}/ml/media/${id}`)).data;
